
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useCallback, useEffect, useMemo, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import NarrowPage from "@/components/layout/narrow-page";
import { Button } from "@/components/ui/button";
import { api } from "@/services/api";
import { useUser } from "@/redux/user";
import SignInPopup from "@/modules/signIn/SignInPopup";
import { useRouter } from "next/router";
type RewardStatus = {
    eligible: boolean;
    reason?: string;
    state?: "ineligible" | "not_started" | "in_progress" | "claimed" | "expired";
    canStart?: boolean;
    canClaim?: boolean;
    requiredGames?: number;
    gamesPlayed?: number;
    rewardDays?: number;
    startedAt?: string;
    windowEnd?: string;
    claimedAt?: string;
};
function toLocalDate(value?: string): string {
    if (!value)
        return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return "";
    return date.toLocaleDateString();
}
function reasonText(reason?: string): string {
    if (reason === "winner_excluded") {
        return "This claim link is only for non-winner participants.";
    }
    if (reason === "not_chessparty_participant") {
        return "No ChessParty participation was found for your account.";
    }
    if (reason === "missing_or_placeholder_user_email") {
        return "Please add a valid email to your account before claiming.";
    }
    if (reason === "banned_account") {
        return "This account is not eligible.";
    }
    return "This account is not eligible for this reward.";
}
export default function ChessPartyClaimPage() {
    const router = useRouter();
    const { isGuest, id: userId } = useUser();
    const [showSignIn, setShowSignIn] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<RewardStatus | null>(null);
    const rewardDays = status?.rewardDays ?? 90;
    const progressText = useMemo(() => {
        const required = status?.requiredGames ?? 3;
        const played = status?.gamesPlayed ?? 0;
        return `${Math.min(played, required)}/${required}`;
    }, [status?.gamesPlayed, status?.requiredGames]);
    const loadStatus = useCallback(async () => {
        if (!userId || isGuest)
            return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get<RewardStatus>("/chessparty/reward/status");
            if (data?.eligible && data.state === "not_started") {
                const started = await api.post<RewardStatus>("/chessparty/reward/start");
                setStatus(started.data);
            }
            else {
                setStatus(data);
            }
        }
        catch (e: any) {
            const code = e?.response?.status;
            if (code === 401) {
                setShowSignIn(true);
            }
            else {
                setError("Unable to load claim status right now.");
            }
        }
        finally {
            setLoading(false);
        }
    }, [isGuest, userId]);
    const claimNow = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.post<RewardStatus>("/chessparty/reward/claim");
            setStatus(data);
        }
        catch {
            setError("Unable to claim reward right now.");
            await loadStatus();
        }
        finally {
            setLoading(false);
        }
    }, [loadStatus]);
    useEffect(() => {
        if (isGuest === false && userId) {
            loadStatus();
        }
    }, [isGuest, loadStatus, userId]);
    return (<>
      <Head>
        <title>ChessHotel | ChessParty Claim</title>
      </Head>
      <NarrowPage title={null}>
        <div className="w-full max-w-xl mx-auto pt-5 pb-8">
          <h1 className="text-xl font-semibold">ChessParty Reward Claim</h1>
          <p className="mt-2 text-sm text-slate-600">
            Play 3 games in 7 days and unlock {rewardDays} days of ChessHotel Basic.
          </p>

          {isGuest && (<div className="mt-6 rounded-xl border border-slate-200 p-4 bg-white">
              <p className="text-sm text-slate-700">
                Log in to claim your challenge.
              </p>
              <div className="mt-3 flex gap-2">
                <Button onClick={() => setShowSignIn(true)} disabled={loading}>
                  Log in to claim
                </Button>
                <Link className="text-sm underline self-center" href="/signIn/passwordRecovery">
                  Forgot password?
                </Link>
              </div>
            </div>)}

          {!isGuest && loading && !status && (<div className="mt-6 text-sm text-slate-600">Loading claim status...</div>)}

          {!isGuest && status?.eligible === false && (<div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              {reasonText(status.reason)}
            </div>)}

          {!isGuest && status?.eligible && status.state === "in_progress" && (<div className="mt-6 rounded-xl border border-sky-200 bg-sky-50 p-4">
              <p className="text-sm font-semibold text-slate-800">Challenge started</p>
              <p className="mt-1 text-sm text-slate-700">
                Progress: {progressText} games
                {status.windowEnd ? ` (ends ${toLocalDate(status.windowEnd)})` : ""}
              </p>
              {status.canClaim && (<Button className="mt-3" onClick={claimNow} disabled={loading}>
                  Claim reward now
                </Button>)}
              {!status.canClaim && (<Button className="mt-3" onClick={() => router.push("/play")}>
                  Play now
                </Button>)}
            </div>)}

          {!isGuest && status?.eligible && status.state === "claimed" && (<div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900">Reward claimed</p>
              <p className="mt-1 text-sm text-emerald-800">
                Your {rewardDays}-day Basic reward is active.
              </p>
              <Button className="mt-3" onClick={() => router.push("/play")}>
                Go to play
              </Button>
            </div>)}

          {!isGuest && status?.eligible && status.state === "expired" && (<div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Challenge expired. If this looks wrong, contact support.
            </div>)}

          {!isGuest && status?.eligible && status.state === "not_started" && (<div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-sm text-slate-700">Challenge not started yet.</p>
              <Button className="mt-3" onClick={loadStatus} disabled={loading}>
                Start challenge
              </Button>
            </div>)}

          {error && <div className="mt-4 text-sm text-amber-800">{error}</div>}
        </div>
      </NarrowPage>
      {showSignIn && (<SignInPopup onClose={() => setShowSignIn(false)} redirectAfterDone={() => {
                setShowSignIn(false);
                loadStatus();
            }} hideSignUp/>)}
    </>);
}

    async function __Next_Translate__getStaticProps__19db54a6135__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/claim/chessparty',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19db54a6135__ as getStaticProps }
  